import { Autocomplete, TextField } from '@mui/material';
import { getBusinessesSummary } from 'lib/api/dashboard';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import Table from 'lib/designSystem/Table';
import { Header } from 'lib/designSystem/TableWithPagination';
import React, { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

const tableHeaders: Header[] = [
  { key: 'name', label: 'Nombre' },
  { key: 'identifier', label: 'Rut' },
  { key: 'statusLabel', label: 'Estado SII', type: 'status-chip' },
  { key: 'activitiesStart', label: 'Inicio Actividades', type: 'date' },
  {
    key: 'averageSalesLast6Months',
    label: 'Prom. Venta U6M',
    type: 'money',
  },
  {
    key: 'averageSalesLast12Months',
    label: 'Prom. Venta U12M',
    type: 'money',
  },
  {
    key: 'creditNotesPercentage',
    label: '% NC',
    type: 'percentage',
  },
  { key: 'creditNotesAmount', label: 'Monto NC', type: 'money' },
  { key: 'cancellationPercentage', label: '% Cancel.', type: 'percentage' },
  { key: 'cancellationAmount', label: 'Monto Cancel.', type: 'money' },
  { key: 'fiscalDebtDue', label: 'Deuda Fiscal', type: 'money' },
  { key: 'crossInvoiceTotal', label: 'Tot. Fact. Cruzadas', type: 'money' },
  { key: 'tgrAlert', label: 'Alerta TGR', type: 'alert' },
  { key: 'ownCessionLastMonthAmount', label: 'Cedido Ult. Mes', type: 'money' },
  {
    key: 'ownCessionLast6MonthsAmount',
    label: 'Cedido Últ. 6 Meses',
    type: 'money',
  },
  {
    key: 'ownCessionLast12MonthsAmount',
    label: 'Cedido Últ. 12 Meses',
    type: 'money',
  },
  {
    key: 'othersCessionLastMonthAmount',
    label: 'Cedido Otros Ult. Mes',
    type: 'money',
  },
  {
    key: 'othersCessionLast6MonthsAmount',
    label: 'Cedido Otros Últ. 6 Meses',
    type: 'money',
  },
  {
    key: 'othersCessionLast12MonthsAmount',
    label: 'Cedido Otros Últ. 12 Meses',
    type: 'money',
  },
];

const ClientsSummary = () => {
  const dispatch = useDispatch();
  const [identifier, setIdentifier] = useState<string | undefined>();
  const { data, isPending } = getBusinessesSummary(dispatch);
  const filteredData = useMemo(() => {
    if (!identifier) return data?.businessesSummary || [];
    return (
      data?.businessesSummary?.filter(
        (business) => business.identifier === identifier,
      ) || []
    );
  }, [data, identifier]);

  const businesses = useMemo(() => {
    return (
      data?.businessesSummary?.map((business) => ({
        label: `${business.name} (${business.identifier})`,
        value: business.identifier,
      })) || []
    );
  }, [data]);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <TableCard sx={{ marginTop: 2 }}>
      <SubtitleWithTooltip
        title="Resumen de clientes"
        tooltipContent="Acá podrás ver el resumen de tus clientes. Si deseas descargar el resumen, haz click en el botón de exportar abajo a la izquierda."
        tooltipPlacement="right"
        variant="compact"
      >
        <Autocomplete
          options={businesses}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => <TextField {...params} label="Cliente" />}
          onChange={(_, newValue) => setIdentifier(newValue?.value)}
          size="small"
          sx={{ width: '300px' }}
        />
      </SubtitleWithTooltip>
      <Table
        data={filteredData}
        dataHeaders={tableHeaders}
        rowsPerPageDefault={10}
        exportToFile="xlsx"
      />
    </TableCard>
  );
};

export default ClientsSummary;
